import { useState } from 'react';
import {
  Sun, Moon, Globe, Bell, Shield, Key, Palette,
  Save, User, Mail, Phone, MapPin, Building,
  Check, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { Card } from '@/components/ui';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/hooks/useTranslation';

export default function SettingsPage() {
  const { t, language } = useTranslation();
  const { theme, setTheme, setLanguage } = useSettingsStore();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved] = useState(false);
  const isRtl = language === 'ar';

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { key: 'general', icon: Palette, label: language === 'ar' ? 'عام' : 'General' },
    { key: 'profile', icon: User, label: language === 'ar' ? 'الحساب' : 'Profile' },
    { key: 'notifications', icon: Bell, label: language === 'ar' ? 'الإشعارات' : 'Notifications' },
    { key: 'security', icon: Shield, label: language === 'ar' ? 'الأمان' : 'Security' },
  ];

  const ArrowIcon = isRtl ? ChevronLeft : ChevronRight;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">{t('settings')}</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar' ? 'إدارة إعدادات التطبيق والحساب' : 'Manage app and account settings'}
          </p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl text-sm font-medium animate-fade-in">
            <Check size={18} />
            {t('saveSuccess')}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-1">
          <Card>
            <nav className="p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all mb-1 ${
                    activeTab === tab.key
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <tab.icon size={20} />
                  <span className="flex-1 text-start">{tab.label}</span>
                  <ArrowIcon size={16} className="opacity-40" />
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* General Settings */}
          {activeTab === 'general' && (
            <>
              <Card title={language === 'ar' ? 'المظهر' : 'Appearance'}>
                <div className="p-6 space-y-6">
                  {/* Theme */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{t('theme')}</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => { setTheme('light'); showSaved(); }}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          theme === 'light'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                          <Sun className="text-amber-600" size={24} />
                        </div>
                        <div className="text-start">
                          <p className="font-semibold text-slate-800 dark:text-white">{t('lightMode')}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'فاتح ونظيف' : 'Light and clean'}</p>
                        </div>
                        {theme === 'light' && <Check className="text-primary-500 ms-auto" size={20} />}
                      </button>
                      <button
                        onClick={() => { setTheme('dark'); showSaved(); }}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          theme === 'dark'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <Moon className="text-indigo-600 dark:text-indigo-400" size={24} />
                        </div>
                        <div className="text-start">
                          <p className="font-semibold text-slate-800 dark:text-white">{t('darkMode')}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'ar' ? 'داكن ومريح' : 'Dark and easy on eyes'}</p>
                        </div>
                        {theme === 'dark' && <Check className="text-primary-500 ms-auto" size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Language */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{t('language')}</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => { setLanguage('ar'); showSaved(); }}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          language === 'ar'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-2xl">
                          🇪🇬
                        </div>
                        <div className="text-start">
                          <p className="font-semibold text-slate-800 dark:text-white">{t('arabic')}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">RTL</p>
                        </div>
                        {language === 'ar' && <Check className="text-primary-500 ms-auto" size={20} />}
                      </button>
                      <button
                        onClick={() => { setLanguage('en'); showSaved(); }}
                        className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          language === 'en'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-2xl">
                          🇺🇸
                        </div>
                        <div className="text-start">
                          <p className="font-semibold text-slate-800 dark:text-white">{t('english')}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">LTR</p>
                        </div>
                        {language === 'en' && <Check className="text-primary-500 ms-auto" size={20} />}
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}

          {/* Profile Settings */}
          {activeTab === 'profile' && (
            <Card title={language === 'ar' ? 'معلومات الحساب' : 'Account Information'}>
              <div className="p-6 space-y-6">
                {/* Avatar */}
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-3xl font-bold">
                    {user?.name?.[0] || 'م'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">{user?.name || 'المدير'}</h3>
                    <p className="text-slate-500 dark:text-slate-400">{user?.role || 'admin'}</p>
                    <button className="mt-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
                      {language === 'ar' ? 'تغيير الصورة' : 'Change photo'}
                    </button>
                  </div>
                </div>

                {/* Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <User size={14} className="inline me-1" />
                      {language === 'ar' ? 'الاسم' : 'Name'}
                    </label>
                    <input
                      type="text"
                      defaultValue={user?.name || 'المدير'}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <Mail size={14} className="inline me-1" />
                      {t('email')}
                    </label>
                    <input
                      type="email"
                      defaultValue={user?.email || 'admin@allista.com'}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <Phone size={14} className="inline me-1" />
                      {t('phone')}
                    </label>
                    <input
                      type="tel"
                      defaultValue="+20 123 456 7890"
                      dir="ltr"
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <MapPin size={14} className="inline me-1" />
                      {t('address')}
                    </label>
                    <input
                      type="text"
                      defaultValue={language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt'}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                      <Building size={14} className="inline me-1" />
                      {language === 'ar' ? 'اسم الشركة' : 'Company Name'}
                    </label>
                    <input
                      type="text"
                      defaultValue="Allista Platform"
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    />
                  </div>
                </div>

                <button onClick={showSaved} className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-primary-500/25">
                  <Save size={18} />
                  {t('save')}
                </button>
              </div>
            </Card>
          )}

          {/* Notifications Settings */}
          {activeTab === 'notifications' && (
            <Card title={language === 'ar' ? 'إعدادات الإشعارات' : 'Notification Preferences'}>
              <div className="p-6 space-y-4">
                {[
                  { label: language === 'ar' ? 'طلب جديد' : 'New Order', desc: language === 'ar' ? 'إشعار عند استلام طلب جديد' : 'Notify when a new order is received', default: true },
                  { label: language === 'ar' ? 'تغيير حالة الطلب' : 'Order Status Change', desc: language === 'ar' ? 'إشعار عند تغيير حالة طلب' : 'Notify when order status changes', default: true },
                  { label: language === 'ar' ? 'سائق جديد' : 'New Driver', desc: language === 'ar' ? 'إشعار عند تسجيل سائق جديد' : 'Notify when a new driver registers', default: true },
                  { label: language === 'ar' ? 'شكوى جديدة' : 'New Complaint', desc: language === 'ar' ? 'إشعار عند استلام شكوى' : 'Notify when a complaint is filed', default: true },
                  { label: language === 'ar' ? 'تقرير يومي' : 'Daily Report', desc: language === 'ar' ? 'إرسال ملخص يومي بالبريد' : 'Send daily summary via email', default: false },
                  { label: language === 'ar' ? 'تحديثات النظام' : 'System Updates', desc: language === 'ar' ? 'إشعارات تحديثات المنصة' : 'Platform update notifications', default: false },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/50">
                    <div>
                      <p className="font-semibold text-slate-800 dark:text-white">{item.label}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{item.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked={item.default} className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-300 dark:bg-slate-600 rounded-full peer peer-checked:bg-primary-500 peer-focus:ring-4 peer-focus:ring-primary-500/20 transition-colors after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full" />
                    </label>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <Card title={language === 'ar' ? 'الأمان' : 'Security'}>
              <div className="p-6 space-y-6">
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                    <Key size={18} />
                    {language === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                        {language === 'ar' ? 'كلمة المرور الحالية' : 'Current Password'}
                      </label>
                      <input
                        type="password"
                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                          {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
                        </label>
                        <input
                          type="password"
                          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                          {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                        </label>
                        <input
                          type="password"
                          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button onClick={showSaved} className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-primary-500/25">
                  <Save size={18} />
                  {language === 'ar' ? 'تحديث كلمة المرور' : 'Update Password'}
                </button>

                <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                  <h4 className="font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                    <Globe size={18} />
                    {language === 'ar' ? 'الجلسات النشطة' : 'Active Sessions'}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30">
                      <div>
                        <p className="font-medium text-slate-800 dark:text-white">{language === 'ar' ? 'هذا الجهاز' : 'This Device'}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Windows - Chrome</p>
                      </div>
                      <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{language === 'ar' ? 'نشط الآن' : 'Active now'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
